import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '../../components/navigation/Sidebar';
import PatientContextBar from '../../components/navigation/PatientContextBar';
import QuickActions from '../../components/navigation/QuickActions';
import SessionManager from '../../components/navigation/SessionManager';
import SatisfactionRatings from './components/SatisfactionRatings';
import ClinicalOutcomes from './components/ClinicalOutcomes';
import BeforeAfterComparison from './components/BeforeAfterComparison';
import TreatmentTimeline from './components/TreatmentTimeline';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import { useAuth } from '../../contexts/AuthContext';

const TreatmentOutcomeSurvey = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('satisfaction');
  const [selectedTreatment, setSelectedTreatment] = useState(null);
  const [completedTreatments, setCompletedTreatments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [surveyProgress, setSurveyProgress] = useState(0);
  const [surveyData, setSurveyData] = useState({
    satisfaction: {},
    outcomes: {},
    beforeAfter: {}
  });

  const treatmentId = searchParams?.get('treatment');

  useEffect(() => {
    loadCompletedTreatments();
  }, [user]);

  useEffect(() => {
    if (treatmentId && completedTreatments?.length > 0) {
      const treatment = completedTreatments?.find(t => t?.value === treatmentId);
      if (treatment) {
        setSelectedTreatment(treatment?.value);
      }
    }
  }, [treatmentId, completedTreatments]);

  useEffect(() => {
    calculateProgress();
  }, [surveyData, activeTab]);

  const loadCompletedTreatments = async () => {
    try {
      setLoading(true);
      // Mock data for completed treatments
      const mockTreatments = [
        {
          value: 'tp-001',
          label: 'Root Canal Treatment - Tooth #14',
          completedDate: '2026-01-05',
          provider: 'Dr. James Wilson'
        },
        {
          value: 'tp-002',
          label: 'Dental Implant - Tooth #19',
          completedDate: '2025-12-20',
          provider: 'Dr. Michael Chen'
        },
        {
          value: 'tp-003',
          label: 'Periodontal Deep Cleaning',
          completedDate: '2025-12-10',
          provider: 'Dr. Emily Rodriguez'
        },
        {
          value: 'tp-004',
          label: 'Crown Placement - Tooth #3',
          completedDate: '2025-11-28',
          provider: 'Dr. Michael Chen'
        }
      ];
      setCompletedTreatments(mockTreatments);
    } catch (err) {
      console.error('Failed to load treatments:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateProgress = () => {
    let totalFields = 0;
    let completedFields = 0;

    // Count satisfaction fields
    const satisfactionFields = ['overallSatisfaction', 'providerRating', 'facilityRating', 'communicationRating', 'painManagement'];
    totalFields += satisfactionFields?.length;
    completedFields += satisfactionFields?.filter(field => surveyData?.satisfaction?.[field])?.length;

    // Count outcome fields
    const outcomeFields = ['painLevel', 'functionalImprovement', 'aestheticSatisfaction', 'qualityOfLife'];
    totalFields += outcomeFields?.length;
    completedFields += outcomeFields?.filter(field => surveyData?.outcomes?.[field])?.length;

    // Count before/after fields
    if (surveyData?.beforeAfter?.beforePhotos?.length > 0) completedFields += 1;
    if (surveyData?.beforeAfter?.afterPhotos?.length > 0) completedFields += 1;
    if (surveyData?.beforeAfter?.symptomImprovement) completedFields += 1;
    totalFields += 3;

    const progress = totalFields > 0 ? Math.round((completedFields / totalFields) * 100) : 0;
    setSurveyProgress(progress);
  };

  const handleTreatmentSelect = (value) => {
    setSelectedTreatment(value);
    navigate(`/treatment-outcome-survey?treatment=${value}`);
  };

  const handleSurveyDataUpdate = (section, data) => {
    setSurveyData(prev => ({
      ...prev,
      [section]: { ...prev?.[section], ...data }
    }));
  };

  const handleSubmitSurvey = async () => {
    if (surveyProgress < 100) {
      alert('Please complete all sections before submitting.');
      return;
    }

    try {
      // Mock submission
      console.log('Submitting survey:', { selectedTreatment, surveyData });
      alert('Thank you for your feedback! Your survey has been submitted successfully.');
      navigate('/patient-portal');
    } catch (err) {
      console.error('Failed to submit survey:', err);
      alert('Failed to submit survey. Please try again.');
    }
  };

  const handleAlertClick = () => {
    // Handle alert click action
    console.log('Alert clicked');
  };

  const handleSessionExpired = () => {
    // Handle session expiration
    console.log('Session expired');
    navigate('/login');
  };

  const handleSessionExtended = () => {
    // Handle session extension
    console.log('Session extended');
  };

  const tabs = [
    { id: 'satisfaction', label: 'Satisfaction', icon: 'Heart' },
    { id: 'outcomes', label: 'Clinical Outcomes', icon: 'Activity' },
    { id: 'comparison', label: 'Before/After', icon: 'ImageIcon' },
    { id: 'timeline', label: 'Timeline', icon: 'Clock' }
  ];

  const currentPatient = {
    name: "Sarah Johnson",
    mrn: "MRN-2024-789456",
    dob: "03/15/1985",
    age: "41 years"
  };

  const selectedTreatmentData = completedTreatments?.find(t => t?.value === selectedTreatment);

  return (
    <div className="flex h-screen bg-background overflow-hidden pt-16">
      <Sidebar isCollapsed={isSidebarCollapsed} onToggleCollapse={setIsSidebarCollapsed} />

      <div className="flex-1 flex flex-col overflow-hidden">
        <PatientContextBar patient={currentPatient} onAlertClick={handleAlertClick} />
        
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6">
            {/* Header */}
            <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2 flex items-center gap-3">
                    <Icon name="ClipboardCheck" size={28} className="text-primary" />
                    Treatment Outcome Survey
                  </h1>
                  <p className="text-muted-foreground">
                    Help us improve by sharing your treatment experience and outcomes
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="ArrowLeft"
                  onClick={() => navigate('/patient-portal')}
                >
                  Back to Portal
                </Button>
              </div>

              {/* Treatment Selection */}
              <div className="mt-6">
                <Select
                  label="Select Completed Treatment"
                  placeholder="Choose a treatment to review"
                  options={completedTreatments}
                  value={selectedTreatment}
                  onChange={handleTreatmentSelect}
                  searchable
                  disabled={loading}
                />
              </div>

              {selectedTreatmentData && (
                <div className="mt-4 bg-primary/5 rounded-lg border border-primary/20 p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Completed Date:</span>
                      <p className="font-medium text-foreground">{selectedTreatmentData?.completedDate}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Provider:</span>
                      <p className="font-medium text-foreground">{selectedTreatmentData?.provider}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Survey Progress:</span>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                          <div
                            className="h-full bg-success transition-all duration-500"
                            style={{ width: `${surveyProgress}%` }}
                          />
                        </div>
                        <span className="font-bold text-foreground">{surveyProgress}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {selectedTreatment ? (
              <>
                {/* Tab Navigation */}
                <div className="bg-card rounded-xl border border-border shadow-elevation-2 overflow-hidden">
                  <div className="border-b border-border overflow-x-auto">
                    <div className="flex">
                      {tabs?.map(tab => (
                        <button
                          key={tab?.id}
                          onClick={() => setActiveTab(tab?.id)}
                          className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap ${
                            activeTab === tab?.id
                              ? 'text-primary border-b-2 border-primary bg-primary/5' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                          }`}
                        >
                          <Icon name={tab?.icon} size={18} />
                          {tab?.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Tab Content */}
                  <div className="p-6">
                    {activeTab === 'satisfaction' && (
                      <SatisfactionRatings
                        treatmentId={selectedTreatment}
                        data={surveyData?.satisfaction}
                        onUpdate={(data) => handleSurveyDataUpdate('satisfaction', data)}
                      />
                    )}
                    {activeTab === 'outcomes' && (
                      <ClinicalOutcomes
                        treatmentId={selectedTreatment}
                        data={surveyData?.outcomes}
                        onUpdate={(data) => handleSurveyDataUpdate('outcomes', data)}
                      />
                    )}
                    {activeTab === 'comparison' && (
                      <BeforeAfterComparison
                        treatmentId={selectedTreatment}
                        data={surveyData?.beforeAfter}
                        onUpdate={(data) => handleSurveyDataUpdate('beforeAfter', data)}
                      />
                    )}
                    {activeTab === 'timeline' && (
                      <TreatmentTimeline
                        treatmentId={selectedTreatment}
                        treatmentData={selectedTreatmentData}
                      />
                    )}
                  </div>
                </div>

                {/* Submit Section */}
                <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-1">Ready to Submit?</h3>
                      <p className="text-sm text-muted-foreground">
                        {surveyProgress === 100
                          ? 'All sections completed. You can submit your survey now.'
                          : `Complete all sections to submit (${surveyProgress}% done)`}
                      </p>
                    </div>
                    <Button
                      variant="success"
                      size="lg"
                      iconName="Send"
                      onClick={handleSubmitSurvey}
                      disabled={surveyProgress < 100}
                    >
                      Submit Survey
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-12 text-center">
                <Icon name="ClipboardList" size={48} className="text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">Select a Treatment</h3>
                <p className="text-muted-foreground">
                  Please select a completed treatment from the dropdown above to begin your survey.
                </p>
              </div>
            )}
          </div>
        </div>

        <QuickActions />
        <SessionManager 
          onSessionExpired={handleSessionExpired}
          onSessionExtended={handleSessionExtended}
        />
      </div>
    </div>
  );
};

export default TreatmentOutcomeSurvey;